import { Breakpoints, CURATION_PATHS, Locale, NavApiPaths } from "./constants";
import { NavParams, NAV_ENVIRONMENT, PUBLICATION_STATES, BREAKING_NEWS_REGIONS, BREAKING_NEWS_LANGUAGES } from "./types";

export default class NavClient {
    environment: NAV_ENVIRONMENT;
    url: string;
    haproxyToken: string;
    host: string;
    locale: Locale;

    constructor({ environment, useAWS, host, locale }: NavParams) {
        // if (!process.env.NAV_HAPROXY_TOKEN) {
        //     console.error("Cannot initialize nav on BCCv8 without a Haproxy Jump token");
        // }

        this.environment = environment;
        this.haproxyToken = process.env.NAV_HAPROXY_TOKEN || "";
        this.host = host || "";
        this.url = environment === "production"
            ? "https://nav-api.bloomberg.com"
            : useAWS
                ? "http://nav-api-staging.cm.bloomberg.com:8080"
                : "https://nav-api-staging.bloomberg.com";
        this.locale = locale || Locale.en;
    }

    getPathForCuration(apiPath: NavApiPaths, breakpoint: Breakpoints = Breakpoints.default) {
        return `${ apiPath }/${ CURATION_PATHS[this.locale][apiPath][breakpoint] }`;
    }

    getTTL(response: Response): number | undefined {
        const [, maxAge] = response.headers?.get("Cache-Control")?.match(/max-age=(\d+)/) || [];

        return maxAge ? Number(maxAge) : undefined;
    }

    /**
     * Maps regions to their corresponding global language flows
     */
    private static readonly REGION_TO_LANGUAGE_MAP: Record<BREAKING_NEWS_REGIONS, BREAKING_NEWS_LANGUAGES> = {
        [BREAKING_NEWS_REGIONS.JAPAN]: BREAKING_NEWS_LANGUAGES.JAPANESE,
        [BREAKING_NEWS_REGIONS.AFRICA]: BREAKING_NEWS_LANGUAGES.ENGLISH,
        [BREAKING_NEWS_REGIONS.ASIA]: BREAKING_NEWS_LANGUAGES.ENGLISH,
        [BREAKING_NEWS_REGIONS.EUROPE]: BREAKING_NEWS_LANGUAGES.ENGLISH,
        [BREAKING_NEWS_REGIONS.GLOBAL]: BREAKING_NEWS_LANGUAGES.ENGLISH,
        [BREAKING_NEWS_REGIONS.MIDDLE_EAST]: BREAKING_NEWS_LANGUAGES.ENGLISH,
        [BREAKING_NEWS_REGIONS.US]: BREAKING_NEWS_LANGUAGES.ENGLISH,
        [BREAKING_NEWS_REGIONS.UK]: BREAKING_NEWS_LANGUAGES.ENGLISH,
        [BREAKING_NEWS_REGIONS.LATAM]: BREAKING_NEWS_LANGUAGES.ENGLISH,
    };

    /**
     * Determines which global language flow to use based on region
     */
    private getLanguage(region?: BREAKING_NEWS_REGIONS): BREAKING_NEWS_LANGUAGES {
        if (!region) {
            return BREAKING_NEWS_LANGUAGES.ENGLISH; // Default to English global when no region specified
        }

        return NavClient.REGION_TO_LANGUAGE_MAP[region] || BREAKING_NEWS_LANGUAGES.ENGLISH;
    }

    /**
     * Gets the appropriate global methods based on language flow
     */
    private getGlobalMethods(language: BREAKING_NEWS_LANGUAGES, state: PUBLICATION_STATES) {
        const methodMap: Record<BREAKING_NEWS_LANGUAGES, () => {
            globalAuthorSession?: Promise<unknown>;
            globalBreakingNews?: Promise<unknown>;
            globalBreakingNewsLiveStream?: Promise<unknown>;
        }> = {
            [BREAKING_NEWS_LANGUAGES.ENGLISH]: () => ({
                globalAuthorSession: this.fetchGlobalEnglishAuthorSession(state),
                globalBreakingNews: this.fetchGlobalEnglishBreakingNews(state),
                globalBreakingNewsLiveStream: this.fetchGlobalEnglishBreakingNewsLiveStream(state),
            }),
            [BREAKING_NEWS_LANGUAGES.JAPANESE]: () => ({
                globalAuthorSession: undefined,
                globalBreakingNews: undefined,
                globalBreakingNewsLiveStream: undefined,
            }),
        };

        const methodFactory = methodMap[language] || methodMap[BREAKING_NEWS_LANGUAGES.ENGLISH];

        return methodFactory();
    }

    /**
     * Promise.all for objects - resolves all promise values in an object
     */
    private async promiseAllObjects<T extends Record<string, Promise<unknown> | undefined>>(
        obj: T
    ): Promise<Record<string, unknown>> {
        const keys = Object.keys(obj);
        const promises = keys.map((key) => obj[key]);

        const results = await Promise.all(promises);

        const resolvedObj: Record<string, unknown> = {};

        keys.forEach((key, index) => {
            resolvedObj[key] = results[index];
        });

        return resolvedObj;
    }

    async fetchMenuItems(state: PUBLICATION_STATES = PUBLICATION_STATES.PUBLISHED) {
        const [menuItemsDesktop, menuItemsMobile] = await Promise.all([
            this.fetchMenuItemsDesktop(state),
            this.fetchMenuItemsMobile(state)
        ]);

        return {
            menuItemsDesktop,
            menuItemsMobile,
        };
    }

    async fetchMenuItemsDesktop(state: PUBLICATION_STATES = PUBLICATION_STATES.PUBLISHED) {
        const path = this.getPathForCuration(NavApiPaths["menu-items"], Breakpoints.desktop);

        try {
            const response = await fetch(`${ this.url }/${ path }?state=${ state }`, {
                headers: {
                    referrerPolicy: "strict-origin-when-cross-origin",
                    referrer: this.host,
                    "X-Haproxy-Jump": this.haproxyToken,
                }
            });

            if (!response.ok) {
                console.error(`Unable to retrieve desktop menu items from ${ this.url }`);

                return null;
            }

            return await response.json();
        } catch (error) {
            console.error(`Error retrieving desktop menu items from ${ this.url }: ${ error }`);

            return null;
        }
    }

    async fetchMenuItemsMobile(state: PUBLICATION_STATES = PUBLICATION_STATES.PUBLISHED) {
        const path = this.getPathForCuration(NavApiPaths["menu-items"], Breakpoints.mobile);

        try {
            const response = await fetch(`${ this.url }/${ path }?state=${ state }`, {
                headers: {
                    referrerPolicy: "strict-origin-when-cross-origin",
                    referrer: this.host,
                    "X-Haproxy-Jump": this.haproxyToken
                }
            });

            if (!response.ok) {
                console.error(`Unable to retrieve mobile menu items from ${ this.url }`);

                return null;
            }

            return await response.json();
        } catch (error) {
            console.error(`Error retrieving mobile menu items from ${ this.url }: ${ error }`);

            return null;
        }
    }

    async fetchFooterMenuItems(state: PUBLICATION_STATES = PUBLICATION_STATES.PUBLISHED) {
        const path = this.getPathForCuration(NavApiPaths["footer-menu-items"]);

        try {
            const response = await fetch(`${ this.url }/${ path }?state=${ state }`, {
                headers: {
                    referrerPolicy: "strict-origin-when-cross-origin",
                    referrer: this.host,
                    "X-Haproxy-Jump": this.haproxyToken
                }
            });

            if (!response.ok) {
                const text = await response.text();

                console.error(`Error fetching footer menu items from ${ this.url }: ${ text }`);

                return null;
            }

            return await response.json();
        } catch (error) {
            console.error(`Error fetching footer menu items from ${ this.url }: ${ error }`);

            return null;
        }
    }

    async fetchBreakingNews(
        region?: BREAKING_NEWS_REGIONS, state: PUBLICATION_STATES = PUBLICATION_STATES.PUBLISHED
    ) {
        const language = this.getLanguage(region);

        // Regional calls (handled by individual methods - they return null if no region)
        const [
            regionalAuthorSession,
            regionalBreakingNews,
            regionalBreakingNewsLiveStream,
        ] = await Promise.all([
            this.fetchRegionalAuthorSession(region, state),
            this.fetchRegionalBreakingNews(region, state),
            this.fetchRegionalBreakingNewsLiveStream(region, state),
        ]);

        // Global calls - dynamically determined by language
        const globalCalls = this.getGlobalMethods(language, state);

        const {
            globalAuthorSession,
            globalBreakingNews,
            globalBreakingNewsLiveStream,
        } = await this.promiseAllObjects(globalCalls);

        const breakingNews = {
            regionalAuthorSession,
            globalAuthorSession,
            regionalBreakingNews,
            globalBreakingNews,
            regionalBreakingNewsLiveStream,
            globalBreakingNewsLiveStream
        };

        return Object
            .entries(breakingNews)
            .filter(([, value]) => value && Object.keys(value).length)
            .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    }

    async fetchRegionalAuthorSession(
        region?: BREAKING_NEWS_REGIONS, state: PUBLICATION_STATES = PUBLICATION_STATES.PUBLISHED
    ) {
        if (!region) {
            return null;
        }

        try {
            const response = await fetch(`${ this.url }/author-session/${ region }?state=${ state }`, {
                headers: {
                    referrerPolicy: "strict-origin-when-cross-origin",
                    referrer: this.host,
                    "X-Haproxy-Jump": this.haproxyToken
                }
            });

            if (!response.ok) {
                const text = await response.text();

                console.error(`Error fetching author session for region ${ region }: ${ text }`);

                return null;
            }

            return await response.json();
        } catch (error) {
            console.error(`Error fetching author session for region ${ region }: ${ error }`);

            return null;
        }
    }

    async fetchGlobalEnglishAuthorSession(state: PUBLICATION_STATES = PUBLICATION_STATES.PUBLISHED) {
        try {
            const response = await fetch(`${ this.url }/author-session/global?state=${ state }`, {
                headers: {
                    referrerPolicy: "strict-origin-when-cross-origin",
                    referrer: this.host,
                    "X-Haproxy-Jump": this.haproxyToken
                }
            });

            if (!response.ok) {
                const text = await response.text();

                console.error(`Error fetching global author session: ${ text }`);

                return null;
            }

            return await response.json();
        } catch (error) {
            console.error(`Error fetching author session for region global: ${ error }`);

            return null;
        }
    }

    async fetchRegionalBreakingNews(
        region?: BREAKING_NEWS_REGIONS, state: PUBLICATION_STATES = PUBLICATION_STATES.PUBLISHED
    ) {
        if (!region) {
            return null;
        }

        try {
            const response = await fetch(`${ this.url }/breaking-news/${ region }?state=${ state }`, {
                headers: {
                    referrerPolicy: "strict-origin-when-cross-origin",
                    referrer: this.host,
                    "X-Haproxy-Jump": this.haproxyToken
                }
            });

            if (!response.ok) {
                const text = await response.text();

                console.error(`Error fetching breaking news for region ${ region }: ${ text }`);

                return null;
            }

            return await response.json();
        } catch (error) {
            console.error(`Error fetching breaking news for region ${ region }: ${ error }`);

            return null;
        }
    }

    async fetchGlobalEnglishBreakingNews(state: PUBLICATION_STATES = PUBLICATION_STATES.PUBLISHED) {
        try {
            const response = await fetch(`${ this.url }/breaking-news/global?state=${ state }`, {
                headers: {
                    referrerPolicy: "strict-origin-when-cross-origin",
                    referrer: this.host,
                    "X-Haproxy-Jump": this.haproxyToken
                }
            });

            if (!response.ok) {
                const text = await response.text();

                console.error(`Error fetching global breaking news: ${ text }`);

                return null;
            }

            return await response.json();
        } catch (error) {
            console.error(`Error fetching breaking news for region global: ${ error }`);

            return null;
        }
    }

    async fetchRegionalBreakingNewsLiveStream(
        region?: BREAKING_NEWS_REGIONS, state: PUBLICATION_STATES = PUBLICATION_STATES.PUBLISHED
    ) {
        if (!region) {
            return null;
        }

        try {
            const response = await fetch(`${ this.url }/breaking-news-live-stream/${ region }?state=${ state }`, {
                headers: {
                    referrerPolicy: "strict-origin-when-cross-origin",
                    referrer: this.host,
                    "X-Haproxy-Jump": this.haproxyToken
                }
            });

            if (!response.ok) {
                const text = await response.text();

                console.error(`Error fetching breaking news livestream for region ${ region }: ${ text }`);

                return null;
            }

            return await response.json();
        } catch (error) {
            console.error(`Error fetching breaking news livestream for region ${ region }: ${ error }`);

            return null;
        }
    }

    async fetchGlobalEnglishBreakingNewsLiveStream(state: PUBLICATION_STATES = PUBLICATION_STATES.PUBLISHED) {
        try {
            const response = await fetch(`${ this.url }/breaking-news-live-stream/global?state=${ state }`, {
                headers: {
                    referrerPolicy: "strict-origin-when-cross-origin",
                    referrer: this.host,
                    "X-Haproxy-Jump": this.haproxyToken
                }
            });

            if (!response.ok) {
                const text = await response.text();

                console.error(`Error fetching global breaking news livestream: ${ text }`);

                return null;
            }

            return await response.json();
        } catch (error) {
            console.error(`Error fetching breaking news livestream for region global: ${ error }`);

            return null;
        }
    }
}
